import { ADVISORY_DOCUMENT_ENDPOINTS } from '../redux/slices/advisory/advisoryConfig';
import { advisoryPageRange, isRecord } from '../advisory/composites/ui/advisoryProvenance';
import type {
  AdvisorySourcePassageRequest,
  AdvisorySourcePassageResult,
} from '../advisory/composites/ui/advisorySourcePassage';

type ReadyPassage = Extract<AdvisorySourcePassageResult, { status: 'ready' }>;

/**
 * gmid serves this read unpaginated, so one marker costs the whole document's
 * chunks. A persisted chunk does not change, so hold the resolved passage for
 * the current auth session instead of re-reading it on every expand. The key carries
 * the token and collection scope the read was authorised under. Identity changes
 * clear the cache, token refreshes require a fresh read, and failures are not cached.
 */
const CACHE_LIMIT = 32;
const passageCache = new Map<string, ReadyPassage>();
let cacheGeneration = 0;

const passageCacheKey = (request: AdvisorySourcePassageRequest, token: string) =>
  JSON.stringify([token, request.documentId, request.chunkId, [...request.collectionIds].sort()]);

/** Drop held passages and invalidate reads still completing for the old session. */
export const clearAdvisorySourcePassageCache = () => {
  cacheGeneration += 1;
  passageCache.clear();
};

/** Read only the cited document, through the existing authenticated gmid gateway.
 * Collection membership is a query scope, never a frontend access grant.
 * A page or a similar quote is not a substitute for the exact persisted chunk.
 */
export async function readAdvisorySourcePassage(
  request: AdvisorySourcePassageRequest,
  token: string,
  signal: AbortSignal
): Promise<AdvisorySourcePassageResult> {
  const generation = cacheGeneration;
  const assertActive = () => {
    if (signal.aborted || generation !== cacheGeneration)
      throw new DOMException('Aborted', 'AbortError');
  };
  assertActive();
  const cacheKey = passageCacheKey(request, token);
  const cached = passageCache.get(cacheKey);
  if (cached) return cached;
  const query = new URLSearchParams({ document_id: request.documentId, scope: 'collection' });
  request.collectionIds.forEach(id => query.append('collection_ids', id));
  try {
    const response = await fetch(`${ADVISORY_DOCUMENT_ENDPOINTS.chunks}?${query}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
      cache: 'no-store',
    });
    assertActive();
    if (response.status === 401 || response.status === 403) return { status: 'denied' };
    if (response.status === 404 || response.status === 503) return { status: 'unavailable' };
    if (!response.ok) return { status: 'failed' };
    const body: unknown = await response.json();
    assertActive();
    if (!isRecord(body) || body.success !== true || !Array.isArray(body.chunks))
      return { status: 'invalid' };
    const matches = body.chunks.filter(
      (chunk): chunk is Record<string, unknown> =>
        isRecord(chunk) &&
        typeof chunk.document_id === 'string' &&
        chunk.document_id.toLowerCase() === request.documentId.toLowerCase() &&
        chunk.chunk_id === request.chunkId
    );
    if (!matches.length) return { status: 'missing' };
    if (
      matches.length !== 1 ||
      typeof matches[0].content !== 'string' ||
      !matches[0].content.trim()
    )
      return { status: 'invalid' };
    const { content, page_start: start, page_end: end } = matches[0];
    const passage: ReadyPassage = {
      status: 'ready',
      text: content,
      pageRange: advisoryPageRange(start, end),
    };
    if (passageCache.size >= CACHE_LIMIT) {
      const oldest = passageCache.keys().next();
      if (!oldest.done) passageCache.delete(oldest.value);
    }
    passageCache.set(cacheKey, passage);
    return passage;
  } catch {
    assertActive();
    return { status: 'failed' };
  }
}
